import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { TiLocation } from "react-icons/ti";
import { useSelector } from "react-redux";

import { t } from "../../translations";
import {
  getLocationData,
  saveLocationToSession
} from "../../utils/locationUtils";
import { createLogger } from "../../utils/logger";
import "../styles/search-bar.css";
import { openNotificationWithIcon } from "./notification-bar";
const logger = createLogger("SearchBar");
// Add this function after the imports and before the SearchBar component
const extractMetropolitanArea = (addressComponents, formattedAddress = "") => {
  let city = null;
  let metropolitanArea = null;
  let province = null;
  let country = null;
  let postalTown = null;
  let sublocality = null;

  // Debug logging
  logger.log("🏙️ Raw address components:", addressComponents);

  addressComponents.forEach((component) => {
    const types = component.types;
    const longName = component.long_name;

    logger.log(`📍 Component: "${longName}" with types: [${types.join(", ")}]`);

    if (types.includes("country")) {
      country = longName;
    } else if (types.includes("administrative_area_level_1")) {
      province = longName;
    } else if (types.includes("administrative_area_level_2")) {
      metropolitanArea = longName;
    } else if (types.includes("locality")) {
      city = longName;
    } else if (types.includes("postal_town")) {
      postalTown = longName;
    } else if (
      types.includes("sublocality_level_1") ||
      types.includes("sublocality")
    ) {
      sublocality = longName;
    }
  });

  // Enhanced fallback logic for Canadian addresses
  if (!metropolitanArea) {
    if (city) {
      // For Canadian cities, use city as metropolitan area
      if (country === "Canada") {
        // Special handling for major Canadian metropolitan areas
        if (city.toLowerCase().includes("vancouver")) {
          metropolitanArea = "Metro Vancouver Regional District";
        } else if (city.toLowerCase().includes("toronto")) {
          metropolitanArea = "Greater Toronto Area";
        } else if (city.toLowerCase().includes("montreal")) {
          metropolitanArea = "Greater Montreal";
        } else if (city.toLowerCase().includes("calgary")) {
          metropolitanArea = "Calgary Metropolitan Area";
        } else if (city.toLowerCase().includes("edmonton")) {
          metropolitanArea = "Edmonton Metropolitan Area";
        } else if (city.toLowerCase().includes("ottawa")) {
          metropolitanArea = "Ottawa-Gatineau Metropolitan Area";
        } else {
          metropolitanArea = `${city} Metropolitan Area`;
        }
      } else {
        metropolitanArea = city;
      }
    } else if (postalTown) {
      metropolitanArea = postalTown;
    } else if (sublocality) {
      metropolitanArea = sublocality;
    } else if (province) {
      metropolitanArea = province;
    } else {
      metropolitanArea = "Unknown";
    }
  }

  // Additional fallback: Check if we can infer from province for Canadian addresses
  if (metropolitanArea === "Unknown" && country === "Canada" && province) {
    if (province === "British Columbia" || province === "BC") {
      metropolitanArea = "Metro Vancouver Regional District"; // Default for BC
    } else if (province === "Ontario" || province === "ON") {
      metropolitanArea = "Greater Toronto Area"; // Default for Ontario
    } else if (province === "Quebec" || province === "QC") {
      metropolitanArea = "Greater Montreal"; // Default for Quebec
    } else if (province === "Alberta" || province === "AB") {
      metropolitanArea = "Calgary Metropolitan Area"; // Default for Alberta
    }
  }

  // Final fallback: Check formatted address for keywords
  if (metropolitanArea === "Unknown" && formattedAddress) {
    const addressLower = formattedAddress.toLowerCase();
    if (
      addressLower.includes("vancouver") ||
      addressLower.includes("bc") ||
      addressLower.includes("british columbia")
    ) {
      metropolitanArea = "Metro Vancouver Regional District";
    } else if (
      addressLower.includes("toronto") ||
      addressLower.includes("ontario")
    ) {
      metropolitanArea = "Greater Toronto Area";
    } else if (
      addressLower.includes("montreal") ||
      addressLower.includes("quebec")
    ) {
      metropolitanArea = "Greater Montreal";
    } else if (
      addressLower.includes("calgary") ||
      addressLower.includes("alberta")
    ) {
      metropolitanArea = "Calgary Metropolitan Area";
    } else if (addressLower.includes("edmonton")) {
      metropolitanArea = "Edmonton Metropolitan Area";
    } else if (addressLower.includes("ottawa")) {
      metropolitanArea = "Ottawa-Gatineau Metropolitan Area";
    }
  }

  const result = {
    city: city || postalTown || "Unknown",
    metropolitan_area: metropolitanArea,
    province: province || "Unknown",
    country: country || "Unknown"
  };

  logger.log("🏙️ Extracted metropolitan area info:", result);

  return result;
};

const SearchBar = (props) => {
  const [load, _setLoad] = useState(false);
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [_locationPermission, setLocationPermission] = useState(null);

  let selectedLang = useSelector((state) => state.lang);

  // Check location permission and load existing location data on component mount
  useEffect(() => {
    checkLocationPermission();

    // Try to load existing location data from URL or session storage
    const existingLocation = getLocationData();
    if (existingLocation) {
      setAddress(existingLocation.address || "");
      setCoordinates(existingLocation.coordinates);
      if (existingLocation.source === "url") {
        logger.log("📍 Location loaded from URL parameters");
      } else {
        logger.log("📍 Location loaded from session storage");
      }
    }
  }, []);

  // Check location permission
  const checkLocationPermission = () => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        setLocationPermission(result.state);
      });
    }
  };

  // Add function to get metropolitan area from place details
  const getPlaceMetropolitanArea = async (placeId) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_API_KEY_MAP}&result_type=locality|administrative_area_level_2|administrative_area_level_1|country|postal_town|sublocality_level_1|sublocality`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        return extractMetropolitanArea(
          data.results[0].address_components,
          data.results[0].formatted_address
        );
      }
    } catch (error) {
      logger.error("Error getting place metropolitan area:", error);
    }

    return {
      city: "Unknown",
      metropolitan_area: "Unknown",
      province: "Unknown",
      country: "Unknown"
    };
  };

  // Handle Google Places Autocomplete selection
  const handlePlaceSelect = async (selectedPlace) => {
    if (selectedPlace) {
      setSelectedPlace(selectedPlace);
      setAddress(selectedPlace.label);

      // Extract coordinates from the place
      const place = selectedPlace.value;
      if (place.geometry && place.geometry.location) {
        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setCoordinates(coords);

        // Extract metropolitan area from place details
        const metropolitanInfo = await getPlaceMetropolitanArea(place.place_id);
        selectedPlace.value.metropolitan_area = metropolitanInfo;

        logger.log("📍 Place selected with coordinates:", coords);
        logger.log("🏙️ Metropolitan area:", metropolitanInfo);
      } else {
        // Fallback: geocode the address to get coordinates and metropolitan area
        const result = await geocodeAddress(selectedPlace.label);
        if (result) {
          setCoordinates(result.coordinates);
          selectedPlace.value.metropolitan_area = result.metropolitan_area;
        }
      }
    }
  };

  // Geocode a specific address to get coordinates using Google Maps API
  const geocodeAddress = async (query) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${process.env.REACT_APP_API_KEY_MAP}&result_type=locality|administrative_area_level_2|administrative_area_level_1|country|postal_town|sublocality_level_1|sublocality`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const coords = {
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng
        };

        const metropolitanInfo = extractMetropolitanArea(
          result.address_components,
          result.formatted_address
        );

        setCoordinates(coords);
        logger.log("📍 Geocoded coordinates:", coords);
        logger.log("🏙️ Metropolitan area:", metropolitanInfo);

        return {
          coordinates: coords,
          metropolitan_area: metropolitanInfo
        };
      }
    } catch (error) {
      logger.error("Geocoding error:", error);
    }
    return null; // Return null if geocoding failed
  };

  // Handle search button click
  const handleSearchClick = async () => {
    if (!address || address.length < 2) {
      return openNotificationWithIcon(
        "info",
        t("errors.add_address", selectedLang)
      );
    }

    // If no coordinates, try to geocode the address first
    if (!coordinates) {
      const geocodeResult = await geocodeAddress(address);
      if (geocodeResult) {
        // Coordinates were set by geocodeAddress, proceed with search
        logger.log("🔍 Passing to backend (geocoded):", {
          address,
          coordinates: geocodeResult.coordinates,
          metropolitan_area: geocodeResult.metropolitan_area
        });

        // Ensure metropolitan area is properly formatted for backend
        const metropolitanAreaForBackend =
          geocodeResult.metropolitan_area?.metropolitan_area ||
          geocodeResult.metropolitan_area?.city ||
          "Unknown";
        logger.log(
          "🏙️ Metropolitan area for backend (geocoded):",
          metropolitanAreaForBackend
        );

        // Save location to session storage for future use
        saveLocationToSession({
          address,
          coordinates: geocodeResult.coordinates,
          metropolitan_area: geocodeResult.metropolitan_area
        });

        // Pass metropolitan area to backend
        props?.setAddress?.(
          address,
          geocodeResult.coordinates,
          geocodeResult.metropolitan_area
        );
        return;
      } else {
        return openNotificationWithIcon(
          "error",
          t("errors.invalid_address", selectedLang)
        );
      }
    }

    // Proceed with search using existing coordinates
    const metropolitanInfo = selectedPlace?.value?.metropolitan_area || {
      city: "Unknown",
      metropolitan_area: "Unknown"
    };

    logger.log("🔍 Passing to backend:", {
      address,
      coordinates,
      metropolitan_area: metropolitanInfo
    });

    // Ensure metropolitan area is properly formatted for backend
    const metropolitanAreaForBackend =
      metropolitanInfo.metropolitan_area || metropolitanInfo.city || "Unknown";
    logger.log("🏙️ Metropolitan area for backend:", metropolitanAreaForBackend);

    // Save location to session storage for future use
    saveLocationToSession({
      address,
      coordinates,
      metropolitan_area: metropolitanInfo
    });

    // Pass metropolitan area to backend
    props?.setAddress?.(address, coordinates, metropolitanInfo);
  };

  return (
    <div className="block-search-bar">
      <div className="search-bar">
        <div
          className="search-bar-int"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            width: "100%",
            padding: "8px",
            borderRadius: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "2px solid transparent",
            transition: "all 0.3s ease"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: "#ff114d",
              fontSize: "24px"
            }}
          >
            <TiLocation />
          </div>

          <div
            style={{
              flex: 1,
              position: "relative",
              minWidth: "0"
            }}
          >
            <GooglePlacesAutocomplete
              apiKey={process.env.REACT_APP_API_KEY_MAP}
              selectProps={{
                placeholder:
                  props?.placeholder ||
                  t("layout.search_placeholder", selectedLang),
                value: selectedPlace,
                onChange: handlePlaceSelect,
                styles: {
                  container: (provided) => ({
                    ...provided,
                    width: "100%"
                  }),
                  control: (provided) => ({
                    ...provided,
                    padding: "8px 12px",
                    fontSize: "16px",
                    border: "none",
                    borderRadius: "8px",
                    outline: "none",
                    boxShadow: "none",
                    backgroundColor: "transparent",
                    minHeight: "48px",
                    "&:hover": {
                      borderColor: "transparent"
                    }
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: "#999",
                    fontSize: "16px"
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: "#333",
                    fontSize: "16px"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#333",
                    fontSize: "16px"
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    padding: "12px 16px",
                    fontSize: "15px",
                    backgroundColor: state.isSelected
                      ? "#ff114d"
                      : state.isFocused
                        ? "#fff5f5"
                        : "white",
                    color: state.isSelected ? "white" : "#333",
                    "&:hover": {
                      backgroundColor: state.isSelected ? "#ff114d" : "#fff5f5"
                    }
                  }),
                  loadingIndicator: (provided) => ({
                    ...provided,
                    color: "#ff114d"
                  }),
                  noOptionsMessage: (provided) => ({
                    ...provided,
                    color: "#666",
                    fontSize: "14px",
                    padding: "16px"
                  })
                }
              }}
              autocompletionRequest={{
                componentRestrictions: { country: "ca" },
                language: selectedLang === "fr" ? "fr" : "en",
                types: ["address"]
              }}
            />
          </div>

          <button
            onClick={handleSearchClick}
            disabled={load || !address || !coordinates}
            style={{
              backgroundColor: address && coordinates ? "#ff114d" : "#ccc",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: address && coordinates ? "pointer" : "not-allowed",
              opacity: load ? 0.7 : 1,
              fontWeight: "bold",
              minWidth: "140px",
              height: "48px",
              fontSize: "16px",
              transition: "all 0.3s ease",
              boxShadow:
                address && coordinates
                  ? "0 2px 8px rgba(255,17,77,0.3)"
                  : "none",
              "&:hover": {
                transform: address && coordinates ? "translateY(-2px)" : "none",
                boxShadow:
                  address && coordinates
                    ? "0 4px 12px rgba(255,17,77,0.4)"
                    : "none"
              }
            }}
            onMouseEnter={(e) => {
              if (address && coordinates) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 4px 12px rgba(255,17,77,0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (address && coordinates) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 2px 8px rgba(255,17,77,0.3)";
              }
            }}
          >
            {load ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              props.text_btn
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
